import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert } from "../components/Alert";
import { CopyButton } from "../components/CopyButton";
import { ApiError, login, verifyMfa } from "../lib/api";
import type { TokenResponse } from "../lib/types";
import { useConfig } from "../state/config-context";
import { useSession } from "../state/session-context";

type LoginStep =
  | { kind: "credentials" }
  | {
      kind: "mfa";
      mfaToken: string;
      setup: boolean;
      secret: string | null;
      otpauthUrl: string | null;
      error: string | null;
    }
  | { kind: "recovery"; codes: string[]; tokens: TokenResponse };

export function LoginPage(): ReactNode {
  const { t, bootstrap } = useConfig();
  const { status, completeLogin } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<LoginStep>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === "authenticated") {
    return <Navigate to="/" replace />;
  }

  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const appName = bootstrap?.app.name ?? "";

  const onCredentials = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await login(email.trim(), password);
      if (data.mfa_required) {
        setStep({
          kind: "mfa",
          mfaToken: data.mfa_token ?? "",
          setup: data.mfa_setup_required === true,
          secret: data.secret ?? null,
          otpauthUrl: data.otpauth_url ?? null,
          error: null,
        });
        return;
      }
      await completeLogin(data);
      navigate(from, { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setBusy(false);
    }
  };

  const onMfaCode = async (code: string): Promise<void> => {
    if (step.kind !== "mfa") {
      return;
    }
    setBusy(true);
    try {
      const data = await verifyMfa(step.mfaToken, code);
      if (data.recovery_codes !== undefined && data.recovery_codes.length > 0) {
        setStep({ kind: "recovery", codes: data.recovery_codes, tokens: data });
        return;
      }
      await completeLogin(data);
      navigate(from, { replace: true });
    } catch (caught) {
      const message =
        caught instanceof ApiError && caught.code === "mfa_invalid_code"
          ? t("mfa.invalid")
          : caught instanceof ApiError
            ? caught.detail
            : t("error.network");
      setStep((current) => (current.kind === "mfa" ? { ...current, error: message } : current));
    } finally {
      setBusy(false);
    }
  };

  const onRecoveryContinue = async (): Promise<void> => {
    if (step.kind !== "recovery") {
      return;
    }
    setBusy(true);
    try {
      await completeLogin(step.tokens);
      navigate(from, { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <header className="auth-card__header">
          <h1 className="auth-card__title">{appName}</h1>

          {step.kind === "credentials" ? (
            <>
              <h2>{t("login.title")}</h2>
              <p className="muted">{t("login.subtitle")}</p>
            </>
          ) : null}

          {step.kind === "mfa" ? (
            <>
              <h2>{step.setup ? t("mfa.setupTitle") : t("mfa.title")}</h2>
              {step.setup ? <p className="muted">{t("mfa.setupHint")}</p> : null}
            </>
          ) : null}

          {step.kind === "recovery" ? <h2>{t("mfa.recoveryTitle")}</h2> : null}
        </header>

        {error !== null ? <Alert tone="error">{error}</Alert> : null}

        {step.kind === "credentials" ? (
          <form className="form" onSubmit={(event) => void onCredentials(event)}>
            <label className="field" htmlFor="login-email">
              <span>{t("login.email")}</span>
              <input
                id="login-email"
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="login-password">
              <span>{t("login.password")}</span>
              <input
                id="login-password"
                type="password"
                name="password"
                autoComplete="current-password"
                minLength={8}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? t("login.submitting") : t("login.submit")}
            </button>
          </form>
        ) : null}

        {step.kind === "mfa" ? (
          <MfaForm
            secret={step.secret}
            otpauthUrl={step.otpauthUrl}
            error={step.error}
            busy={busy}
            onSubmit={(code) => void onMfaCode(code)}
            onBack={() => {
              setError(null);
              setStep({ kind: "credentials" });
            }}
          />
        ) : null}

        {step.kind === "recovery" ? (
          <RecoveryPanel
            codes={step.codes}
            busy={busy}
            onContinue={() => void onRecoveryContinue()}
          />
        ) : null}
      </div>
    </div>
  );
}

function MfaForm({
  secret,
  otpauthUrl,
  error,
  busy,
  onSubmit,
  onBack,
}: {
  secret: string | null;
  otpauthUrl: string | null;
  error: string | null;
  busy: boolean;
  onSubmit: (code: string) => void;
  onBack: () => void;
}): ReactNode {
  const { t } = useConfig();
  const [code, setCode] = useState("");

  return (
    <div className="form">
      {secret !== null ? (
        <div className="credential-block">
          <p className="credential-block__label">{t("mfa.secret")}</p>
          <div className="credential-block__row">
            <code className="break-all">{secret}</code>
            <CopyButton value={secret} />
          </div>
        </div>
      ) : null}

      {otpauthUrl !== null ? (
        <div className="credential-block">
          <p className="credential-block__label">{t("mfa.otpauth")}</p>
          <div className="credential-block__row">
            <code className="break-all">{otpauthUrl}</code>
            <CopyButton value={otpauthUrl} />
          </div>
        </div>
      ) : null}

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (code.length === 6) {
            onSubmit(code);
          }
        }}
      >
        <label className="field" htmlFor="mfa-code">
          <span>{t("mfa.code")}</span>
          <input
            id="mfa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
          />
        </label>
        <div className="form-actions">
          <button type="button" className="btn btn--ghost" onClick={onBack} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy || code.length !== 6}>
            {busy ? t("mfa.submitting") : t("mfa.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

function RecoveryPanel({
  codes,
  busy,
  onContinue,
}: {
  codes: string[];
  busy: boolean;
  onContinue: () => void;
}): ReactNode {
  const { t } = useConfig();
  return (
    <div className="form">
      <h3 className="recovery-title">{t("mfa.recoveryTitle")}</h3>
      <Alert tone="info">{t("mfa.recoveryHint")}</Alert>
      <ul className="recovery-codes">
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ul>
      <div className="form-actions">
        <CopyButton value={codes.join("\n")} />
        <button type="button" className="btn btn--primary" onClick={onContinue} disabled={busy}>
          {busy ? t("app.loading") : t("common.confirm")}
        </button>
      </div>
    </div>
  );
}
